import { IDisposable } from "Global/Base/IDisposable";
import { AwaitableStandardEvent, Event } from "../../../../Global/Base/Events";
import { IOWObjectList } from "../../../../OW.Remoting/Remoting/DataTypes/IOWObjectList";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { IRightConditionItem } from "../../../../OW.Remoting/Remoting/Rights";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { IFormRight } from "../../Rights/FormRight";
import { IGenericControl, IVersioned } from "../Controls/ControlInterfaces";
import { SidePanel } from "../Controls/SidePanel";
import { IFormUI } from "../FormUI/FormUI";
import { FormContext, IWindowable } from "../UIManagerInterfaces";
import { OpenModeType } from "./Actions/DetailFormAction";
import { FilterPanel } from "./Filters/FilterPanel";
import { IFormControl } from "./FormControl";
import { IFormProfileInfo } from "./Forms/IFormProfileInfo";
import { IFormProfileManager } from "./Forms/IFormProfileManager";
import { IHideButtons } from "./Frames";
import { IMasterableFrame } from "./Frames/FrameInterfaces";
import { IDetailFrame } from "./Frames/IDetailMasterableFrame";
import { IFrame } from "./IFrame";

export enum FrameRefreshType {
    none,
    recordOnChange,
    listOnChange,
}

export enum FormLayout {
    none,
    vertical,
    horizontal,
}

/**
 * Rozhraní představující jeden aplikační formulář
 * @includeToDoc
 */
export interface IForm extends IGenericControl, IWindowable, IFormControl, IVersioned, IUsingFormUI {
    /** Odkaz na správce profilů */
    readonly profileManager: IFormProfileManager | undefined;
    // form
    formName: string;
    /**
     * Identifikátor otevřené instance formuláře.
     */
    readonly formInstanceId: string;
    /**
     * Informace o tom, zde jde o "detailový formulář".
     * Tedy že z formuláře je zachován pouze DetailFrame a je napojen na hlavní frame předaný jako detailFormMasterFrame
     */
    readonly isDetailForm: boolean;
    /**
     * Informace o tom, zda je detail form otevren v multieditačním modu, tj. na nadřazeném frame, bylo vybráno vic objektu a přepnuto do editace v samostatném okně
     */
    readonly multiEditMode: boolean;
    /** Verze definice formuláře */
    readonly version?: string;
    locKey: string;
    profileId: string;
    formUI: IFormUI;
    filterPanel: FilterPanel;
    formSettingsPanel: SidePanel;
    showDetail: boolean;
    setAttributesOnNewObject: string;
    /** Název frame, který bude použit místo hlavního pro LOV */
    readonly lovFrame: string | undefined;
    viewMode: boolean;
    /** Indikuje, zdali se formulář má otevřít v Preview módu, kdy se načítá custom definice formuláře ze serveru a je potlačena cache. */
    isPreviewMode?: boolean;
    /**
     * Zda má být viditelný pouze DetailFrame.
     * U detailových formulářů je tato vlastnost implicitně true, jinak false.
     * Nastavení na jinou hodnotu než undefined přebíjí implicitní nastavení.
     */
    readonly visibleOnlyDetail: boolean;
    /** Zda mají být skryty akce pro otevření do nového okna/záložky */
    hideWindowActions: boolean;
    detailFrame: IDetailFrame;
    openInEdit: boolean;
    readOnly: boolean;
    isEdited: boolean;
    canClose: boolean;
    canNew: boolean;
    canDelete: boolean;
    canRead: boolean;
    canInsertAtFormLevel: boolean;
    openMode: OpenModeType;
    purposeType?: MenuType;
    readVersioned: boolean;
    /** Název třídy zakládaného záznamu při OpenMode.insert */
    readonly insertObjectName?: string;
    /**
     * Kolekce všech framů, které mohou mít podřízený frame
     */
    masterableFrames: ReadonlyArray<IMasterableFrame>;
    /**
     * Kolekce všech framů
     */
    frames: ReadonlyArray<IFrame>;
    /**
     * Nastavení profilu pro formulář
     */
    profileInfo: IFormProfileInfo;

    // prava
    formRights: IFormRight[];
    conditionItems: IRightConditionItem[];
    usedConditionItemsId: string[];

    // pomocne promenne, asi by tu z logickeho hlediska ani nemely byt
    initialFormProfileId: number;
    /**
     * Frame, který se nastaví jako nadřízený frame DetailFramu na otevíraném formuláři.
     * DetailFrame pak zobrazuje a pracuje se záznamem vybraným v nadřízeném framu.
     * Možné použít pouze pro detailové formuláře.
     */
    readonly detailFormMasterFrame?: IMasterableFrame;
    /**
     * Aplikační záznam, který bude zobrazen v detailovém formuláři.
     * Záznam zobrazovaný na detailovém formuláři se bere z detailFormMasterFrame nebo z této vlastnosti.
     */
    readonly detailFormObject?: OWObject;
    masterObject?: OWObject;
    /**
     * Vlastnost umožňující předání více vybraných aplikačních záznamů do formuláře současně.
     */
    masterObjectList?: IOWObjectList;
    /** Podmínka omezující načítaná data do hlavního framu formuláře */
    formCondition?: string;
    /** Názvy aplikačních atributů, v nichž jsou hodnoty k parametrům podmínky */
    formConditionParamAttrs?: readonly string[];
    /** Instance aplikačního záznamu, ze kterého se načítají hodnoty parametrů podmínky */
    formConditionParamsObject?: OWObject;
    masterLinkToAttr?: string;
    selectedId?: string;
    autoSelectFirstRow?: boolean;
    autoSelectOnlyIfOneRow?: boolean;
    isLov?: boolean;
    enableLovSelect?: boolean;
    /** Formulář je otevřen do modálního okna */
    isModal?: boolean;
    /** Titulek formu. Má prioritu před titleLocKey */
    customTitle?: string;
    /** Titulek formu ve formě lokalizačního klíče. Zobrazen jen pokud není nastaven customTitle */
    titleLocKey?: string;
    /** Pokud je nastaveno na true, tak formulářové menu nebude k dispozici */
    disableFormMenu?: boolean;
    /**
     * Určuje, zda a jakým způsobem má dojít k refreshi framu nastaveném v frameToRefreshAfterSetFormActive
     */
    frameRefreshType?: FrameRefreshType;
    /** Režim formuláře, ve kterém nejsou při jeho otevření načteny žádná data */
    queryMode?: boolean | null;
    /** Vrací kontext formuláře spolu s rolemi, které měly právo formulář zobrazit v menu */
    formContext: FormContext;

    /** Seznam, který provede RefreshActualObject v momentě, kdy se aktivuje jeho form při práci s modálními okny */
    frameToRefreshAfterSetFormActive?: IFrame;
    horizontalLayout?: boolean;
    setValues: string;
    /**
     * Nastavení viditelnosti tlačítek na hlavním framu, které přepisuje nastavení z formuláře
     */
    hideButtons?: IHideButtons;

    readonly isFormVisible: boolean;

    // udalosti
    /** Je vyvolána v případě, kdy došlo ke změně caption */
    titleChanged: Event<IForm, string>;
    childOpened: Event<IWindowable, void>;
    childClosed: Event<IWindowable, void>;
    onDoOpen: Event<IWindowable, void>;
    readOnlyChanged: Event<IForm, boolean>;
    masterObjectChanged: Event<IForm, OWObject>;
    framesInitialized: Event<IForm, void>;
    /** Kontrola, jestli něco nepřeruší zavírání okna. */
    closingPreview: AwaitableStandardEvent<IWindowable, void>;
    /** Událost vyvolaná při přerušení zavírání okna. */
    closingCancelled: Event<IWindowable, IFormClosingCancelledEventArgs>;
    /** Pokud je nastaveno na true, tak parametrický formulář vrací svou aplikační instanci. Volající se stává zodpovědným za její uvolnění! */
    readonly returnMainInstance?: boolean;
    /** Příznak, zda se jedná o implicitní formulář. Viz https://teaf.tescosw.cz/wiki/index.php?title=Implicitn%C3%AD_formul%C3%A1%C5%99 */
    readonly isImplicit: boolean;

    // metody
    /**
     * Inicializační metoda, přes kterou je předáné formUI
     * @param formUI Předané formUI
     */
    init(formUI: IFormUI): Promise<void>;
    /**
     * Inicializace formuláře ve store. Je potřeba pro správné fungování
     * všech react komponent na formuláři.
     */
    initReduxState(): void;
    actualizeForm(): Promise<void>;
    setVisibleFrame(frame: IFrame, addToUiHistory?: boolean): void;
    setVisibleFrame(frame: string, addToUiHistory?: boolean): void;

    /**
     * Zkontroluje, zda je formulář rozeditovany. Jestli jo, tak zobrazi YNC dialog, jestli se má uložit, zrušit změny nebo nedělat nic.
     * Když se otevíra nový formulář (žadny frame není rozeditovany anebo v YNC dialogu bylo vybrano Ano/Ne), vrací true.
     * Když se zůstavá na současném formuláři (v dialogu zvoleno Storno), tak vrací false.
     * @param param - nastavení režimu ukládání framů
     */
    checkAndSaveForm(param?: IFormSavingParameters): Promise<boolean>;
    setToolbarToEditMode(inEdit: boolean): Promise<void>;
    /**
     * Počká na probíhající procesy na předaném framu a jeho podřízených framech
     * @param sourceFrame Frame, na kterém má začít čekání
     */
    waitForBussyFrameAndChildren(sourceFrame: IFrame): Promise<void>;
    /**
     * Zruší aktivní profil a nastaví formulář do výchozího stavu
     */
    clearProfileInfo(): void;
    /**
     * Přidá side panel
     * @param panel hmtlElement přidávaného panelu
     */
    addSidePanel(panel: HTMLElement): void;

    /**
     * Transakční kontext formuláře
     */
    readonly transactionContextGetter?: () => string | undefined;

    /**
     * Adds the object to disposal along with the form.
     * @param object Disposable object.
     */
    addDisposable?: (object: IDisposable) => void;
}

export class IForm {
    private static _properties: (keyof IForm)[] = [
        "formName", "formUI", "filterPanel", "formSettingsPanel", "showDetail", "viewMode", "visibleOnlyDetail", "detailFrame",
        "openInEdit", "readOnly", "canClose", "openMode", "readVersioned", "formRights", "conditionItems", "usedConditionItemsId",
        "titleChanged", "childOpened", "childClosed", "onDoOpen", "readOnlyChanged", "masterObjectChanged", "setValues", "canInsertAtFormLevel", "profileInfo", "masterableFrames",
    ];
    private static _methods: (keyof IForm)[] = ["init", "actualizeForm", "setVisibleFrame", "checkAndSaveForm", "setToolbarToEditMode", "waitForBussyFrameAndChildren", "clearProfileInfo"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Interface ukazujici na pouziti formUI
 */
export interface IUsingFormUI {
    formUI: IFormUI;
}

export class IUsingFormUI {
    private static _properties: (keyof IForm)[] = [
        "formUI",
    ];
    private static _methods: (keyof IForm)[] = [];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Nastaveni parametrů pro Form při ukládání framů na formuláři prostřednictvím metody checkAdnSafeForm
 */
export interface IFormSavingParameters {
    /**
     * pokud je vyplněn tak se na zadaném formu nekontroluje, jestli je změněný
     */
    sourceFrame?: IMasterableFrame;
    /**
     * pokud není nic změmeno, ale frame je v editaci, tak pokud je atribut true, dojde k jejímu zrušení
     */
    cancelEditIfNotEdited?: boolean;
    /**
     * zda se maji kontrolovat doporučená pole
     */
    checkRecommended?: boolean;
    /**
     * zda se mají kontrolovat povinně odložená pole
     */
    checkPostponed?: boolean;
    /**
     * pokud je nastaveno, přeskočí se YNC dialog a případné změny se automaticky uloží
     */
    forceSave?: boolean;
    /**
     * Pokud je nastaveno na true, tak je dotaz vyvolanpři zavírání formu. Slouží k tomu, aby si zobrazení YNC hlídal každý frame sám.
     */
    formClosing?: boolean;
    /**
     * Ukazatel zdali se má vynechat ukládání rozpracovaného editačního kontextu na framu
     * @see IFrameContext
     */
    skipSavingEditContextId?: boolean;
    /**
     * Zahodí všechny změny na editovaných framech.
     * Hodí se například při rušení editačního kontextu.
     */
    cancelChanges?: boolean;
    /**
     * Otevřené modální MasterableFramy.
     * Hodí se například při ukládání a rušení editačního kontextu,
     * aby se projevily změny i na těchto modálních framech.
     */
    openedModalFrames?: readonly IMasterableFrame[];
    /**
     * Pokud je vyplněn, tak se změny kontrolují pouze na tomto framu a řetězci jeho podřízených framů.
     */
     mostTopFrame?: IMasterableFrame;
}

/**
 * Důvod přerušení zavírání formuláře
 */
export enum FormClosingCancellationReason {
    confirmOnCloseAborted = "confirmOnCloseAborted",
    yncAborted = "yncAborted",
}

/**
 * Interface objektu předávaného vyvolané události closingCancelled
 */
export interface IFormClosingCancelledEventArgs {
    cancellationReason: FormClosingCancellationReason;
}
